import type { IconType } from "react-icons";
import { FaCheck, FaRegComment, FaRegFile } from "react-icons/fa";
import {
  type AwardCriterionDto,
  AwardCriterionType,
  AwardSubCriterionAnswerFormat,
  ContractNature,
  EspdCriterionCategory,
  FoaPart,
  FrameworkAgreementType,
  MinimumRequirementResponseType,
  ProcedureType,
  Regulation,
  ReservedParticipationType,
  ReservedExecutionType,
  SelectionCriterionType,
  WeightType,
} from "../../../../../autogen/bff-api";

// Each OPTIONS array drops the redundant `labelKey` field — consumers call
// `t(option.value)` directly, with SCREAMING_SNAKE_CASE keys living in
// `webapp/locales/{en,no}/translation.json`. Description text remains
// phrase-keyed because it does not map cleanly onto enum values.
//
// Enum variants are pinned for langen by `src/autogen/EnumKeys.tsx`
// (regenerated from `bff-api.ts` by `codegen.ts`). To make a new BFF enum
// translatable, add it to `TRANSLATED_ENUMS` in `codegen.ts`.

// ── Framework Agreement ──────────────────────────────────────────────

// t('Ordering directly from the agreement without new competition')
// t('New competition among the suppliers on the framework agreement')
// t('Both direct call-off and mini-competition, depending on value')
export const FRAMEWORK_AGREEMENT_OPTIONS = [
  {
    value: FrameworkAgreementType.WithoutReopening,
    descriptionKey: "Ordering directly from the agreement without new competition",
  },
  {
    value: FrameworkAgreementType.WithReopening,
    descriptionKey: "New competition among the suppliers on the framework agreement",
  },
  {
    value: FrameworkAgreementType.Mixed,
    descriptionKey: "Both direct call-off and mini-competition, depending on value",
  },
] as const;

export const isFrameworkEnabled = (value: FrameworkAgreementType | null | undefined): boolean =>
  value != null && value !== FrameworkAgreementType.None;

// ── Regulation ───────────────────────────────────────────────────────

// Which regulation governs follows from what the contract serves, not from what kind of body the
// buyer is: forsyningsforskriften § 1-1 covers contracts "knyttet til utøvelsen av" the utility
// activities listed in §§ 1-3 to 1-9. A municipality running a waterworks therefore buys pipes
// under forsyningsforskriften and school furniture under FOA. The firmakort carries the buyer's
// usual choice as a default; this control overrides it per procurement.
// Konsesjonskontraktforskriften is not offered yet.
// t('Ordinary procurements — everything not tied to a utility activity')
// t('Procurements tied to a utility activity: water, energy, transport, ports, airports and post')
export const REGULATION_OPTIONS = [
  { value: Regulation.Foa, descriptionKey: "Ordinary procurements — everything not tied to a utility activity" },
  {
    value: Regulation.Forsyningsforskriften,
    descriptionKey: "Procurements tied to a utility activity: water, energy, transport, ports, airports and post",
  },
] as const;

// The regulations a buyer can actually choose, anywhere. Konsesjonskontraktforskriften exists in the
// BFF enum but has no procedure support, so offering it — here or on the firmakort — would let a
// buyer pick a default that no procurement can ever be announced under.
export const SUPPORTED_REGULATIONS: readonly Regulation[] = REGULATION_OPTIONS.map((option) => option.value);

// ── Contract Nature ──────────────────────────────────────────────────

// t('Purchase of goods or products') t('Purchase of services') t('Building and construction work')
export const CONTRACT_NATURE_OPTIONS = [
  { value: ContractNature.Supplies, descriptionKey: "Purchase of goods or products" },
  { value: ContractNature.Services, descriptionKey: "Purchase of services" },
  { value: ContractNature.Works, descriptionKey: "Building and construction work" },
] as const;

// ── Procedure Type ───────────────────────────────────────────────────

// t('All qualified suppliers can submit bids') t('Pre-qualification before bid submission')
// t('Negotiation with selected suppliers') t('Dialogue phase before final bids')
// t('Develops and buys something the market does not offer yet')
// NegotiatedWithPriorCall is the del III procedure that maps to the eForms code neg-w-call;
// CompetitiveNegotiation has no eForms code and is reserved for the del II variant.
export const PROCEDURE_TYPE_OPTIONS = [
  { value: ProcedureType.Open, descriptionKey: "All qualified suppliers can submit bids", enabled: true },
  { value: ProcedureType.Restricted, descriptionKey: "Pre-qualification before bid submission", enabled: true },
  { value: ProcedureType.NegotiatedWithPriorCall, descriptionKey: "Negotiation with selected suppliers", enabled: true },
  { value: ProcedureType.CompetitiveDialogue, descriptionKey: "Dialogue phase before final bids", enabled: false },
] as const;

// Forsyningsforskriften § 9-1: (1) the main procedures, freely chosen; (2) innovation partnership.
// The § 9-2 and § 9-3 exception routes are separate deliveries, as their FOA counterparts are.
const UTILITIES_PROCEDURE_TYPE_OPTIONS = [
  { value: ProcedureType.Open, descriptionKey: "All qualified suppliers can submit bids", enabled: true },
  { value: ProcedureType.Restricted, descriptionKey: "Pre-qualification before bid submission", enabled: true },
  { value: ProcedureType.NegotiatedWithPriorCall, descriptionKey: "Negotiation with selected suppliers", enabled: true },
  { value: ProcedureType.CompetitiveDialogue, descriptionKey: "Dialogue phase before final bids", enabled: false },
  {
    value: ProcedureType.InnovationPartnership,
    descriptionKey: "Develops and buys something the market does not offer yet",
    enabled: false,
  },
] as const;

export type ProcedureTypeOption = (typeof PROCEDURE_TYPE_OPTIONS)[number] | (typeof UTILITIES_PROCEDURE_TYPE_OPTIONS)[number];

export const procedureTypeOptionsFor = (
  regulation: Regulation | null | undefined,
): readonly ProcedureTypeOption[] =>
  regulation === Regulation.Forsyningsforskriften ? UTILITIES_PROCEDURE_TYPE_OPTIONS : PROCEDURE_TYPE_OPTIONS;

// t('Permitted without a separate justification — forsyningsforskriften § 9-1')
// t('For developing and buying innovative work — forsyningsforskriften § 9-1 (2)')
export const procedureProvisionKey = (
  regulation: Regulation | null | undefined,
  procedureType: ProcedureType,
): string | null => {
  if (regulation !== Regulation.Forsyningsforskriften) return null;
  return procedureType === ProcedureType.InnovationPartnership
    ? "For developing and buying innovative work — forsyningsforskriften § 9-1 (2)"
    : "Permitted without a separate justification — forsyningsforskriften § 9-1";
};

// Forsyningsforskriften's counterparts to the FOA paragraphs this copy cites are unverified, so they
// are omitted there rather than guessed.
export const citesFoaProvisions = (regulation: Regulation | null | undefined): boolean =>
  regulation !== Regulation.Forsyningsforskriften;

// FOA § 13-2 requires a stated ground; forsyningsforskriften § 9-1 (1) does not.
export const requiresProcedureJustification = (regulation: Regulation | null | undefined): boolean =>
  regulation !== Regulation.Forsyningsforskriften;

// ── Reserved Participation ──────────────────────────────────────────

// t('No restriction on participation') t('Reserved for sheltered workshops and similar enterprises')
// t('Reserved for organizations with a public service mission')
export const RESERVED_PARTICIPATION_OPTIONS = [
  { value: ReservedParticipationType.None, descriptionKey: "No restriction on participation" },
  { value: ReservedParticipationType.ShelteredWorkshop, descriptionKey: "Reserved for sheltered workshops and similar enterprises" },
  { value: ReservedParticipationType.PublicServiceMission, descriptionKey: "Reserved for organizations with a public service mission" },
] as const;

// ── Reserved Execution ──────────────────────────────────────────────

// t('No restriction on execution') t('Execution reserved for sheltered employment programmes')
// t('Reservation status not yet determined')
export const RESERVED_EXECUTION_OPTIONS = [
  { value: ReservedExecutionType.None, descriptionKey: "No restriction on execution" },
  { value: ReservedExecutionType.ShelteredEmployment, descriptionKey: "Execution reserved for sheltered employment programmes" },
  { value: ReservedExecutionType.NotKnown, descriptionKey: "Reservation status not yet determined" },
] as const;

// ── Selection Criterion Type ────────────────────────────────────────
//
// "Selection criteria" (EU-direktiv 2014/24/EU) === "kvalifikasjonskrav" (FOA).
// The DTO field is `selectionCriteria`; the UI says "Kvalifikasjonskrav".
// See docs/adr/002-qualification-requirements.md (Decision 1).
export const SELECTION_CRITERION_TYPE_OPTIONS = [
  { value: SelectionCriterionType.Suitability, badgeBg: "smSecondarySoft", badgeColor: "smSecondary" },
  { value: SelectionCriterionType.EconomicFinancial, badgeBg: "smQuaternarySoft", badgeColor: "smQuaternary" },
  { value: SelectionCriterionType.TechnicalProfessional, badgeBg: "smPrimarySofter", badgeColor: "smPrimary" },
  { value: SelectionCriterionType.QualityEnvironment, badgeBg: "smTertiarySoft", badgeColor: "smTertiary" },
  { value: SelectionCriterionType.Other, badgeBg: "smBackgroundSecondary", badgeColor: "smMuted" },
] as const;

// ── Minimum Requirement Response Type ───────────────────────────────
//
// "Minstekrav" — the answer format a supplier must give. Single source for the drawer cards
// (labelKey + descriptionKey + hintKey) and the list/summary badge (icon + labelKey + colors).
export const MINIMUM_REQUIREMENT_RESPONSE_TYPE_OPTIONS: {
  value: MinimumRequirementResponseType;
  labelKey: string;
  descriptionKey: string;
  hintKey: string | null;
  icon: IconType;
  badgeBg: string;
  badgeColor: string;
}[] = [
  {
    value: MinimumRequirementResponseType.YesNo,
    labelKey: "Yes / No",
    descriptionKey: "A simple yes or no answer.",
    hintKey: null,
    icon: FaCheck,
    badgeBg: "smStatusOkBg",
    badgeColor: "smStatusOkText",
  },
  {
    value: MinimumRequirementResponseType.YesNoComment,
    labelKey: "Yes / No + comment",
    descriptionKey: "Yes or no, with room for an explanatory comment.",
    hintKey: "Respondent gets a comment field",
    icon: FaRegComment,
    badgeBg: "smStatusOkBg",
    badgeColor: "smStatusOkText",
  },
  {
    value: MinimumRequirementResponseType.YesNoDocument,
    labelKey: "Yes / No + document",
    descriptionKey: "Yes or no, with the document that proves it.",
    hintKey: "Respondent must attach a document on Yes",
    icon: FaRegFile,
    badgeBg: "smStatusOkBg",
    badgeColor: "smStatusOkText",
  },
];

// ── Award Criterion Model ───────────────────────────────────────────
//
// Note: values are BFF DTO discriminator fields (`eventData.awardCriteria.lowestPrice`),
// not enum members — so they keep phrase-keyed labels.

// t('Lowest price') t('Award based on lowest price alone')
// t('Best price-quality ratio') t('Award based on weighted criteria including quality')
export const AWARD_CRITERION_MODEL_OPTIONS = [
  {
    value: "lowestPrice" as const,
    labelKey: "Lowest price",
    descriptionKey: "Award based on lowest price alone",
  },
  {
    value: "bestPriceQualityRatio" as const,
    labelKey: "Best price-quality ratio",
    descriptionKey: "Award based on weighted criteria including quality",
  },
] as const;

export type AwardCriterionModel = (typeof AWARD_CRITERION_MODEL_OPTIONS)[number]["value"];

// ── Award Criterion Type (per criterion) ────────────────────────────

// The environment sub-types collapse into one "Miljø" choice in the type picker. The buyer never picks
// criteria/spec manually — the BFF derives which of those two is stored (and thus which Doffin
// award-criterion-type code eForms emits) from the total environment weighting. The three § 5c
// exemptions are explicit "no environmental weighting" choices (never weight-derived), chosen via the
// exemption control and rendered as the § 5b/§ 5c justification box rather than a criterion card.
export const ENVIRONMENT_GROUP_VALUE = "ENVIRONMENT";

export const AWARD_CRITERION_TYPE_OPTIONS: { value: string; labelKey: string }[] = [
  { value: AwardCriterionType.Price, labelKey: AwardCriterionType.Price },
  { value: AwardCriterionType.Cost, labelKey: AwardCriterionType.Cost },
  { value: AwardCriterionType.Quality, labelKey: AwardCriterionType.Quality },
  { value: ENVIRONMENT_GROUP_VALUE, labelKey: AwardCriterionType.QualityEnvironmentalCriteria },
];

// ── Answer format (per sub-criterion) ───────────────────────────────

// How the supplier is asked to answer a requirement inside a criterion. Declaration order is the order
// the dropdown offers them: the two open formats first, then the two constrained ones.
export const AWARD_SUB_CRITERION_ANSWER_FORMAT_OPTIONS: readonly AwardSubCriterionAnswerFormat[] = [
  AwardSubCriterionAnswerFormat.FreeText,
  AwardSubCriterionAnswerFormat.Document,
  AwardSubCriterionAnswerFormat.Number,
  AwardSubCriterionAnswerFormat.YesNoPartial,
];

// Award-criterion types that count as climate/environment weighting under § 5b (the "green" paths
// A/B) — mirrors the BFF weighting set and drives the green badge colour.
export const ENVIRONMENTAL_AWARD_CRITERION_TYPES: readonly AwardCriterionType[] = [
  AwardCriterionType.QualityEnvironmentalCriteria,
  AwardCriterionType.QualityEnvironmentalSpecification,
];

// The three § 5c exemptions, mutually exclusive. Each is an explicit "no environmental weighting"
// choice stored as a single marker criterion (never weight-derived). They replaced the single
// pre-2026 exemption when FOA § 7-9 was superseded on 2026-07-01.
export const ENVIRONMENT_EXEMPTION_TYPES: readonly AwardCriterionType[] = [
  AwardCriterionType.QualityEnvironmentalExemptInsignificant,
  AwardCriterionType.QualityEnvironmentalExemptCriticalInterest,
  AwardCriterionType.QualityEnvironmentalExemptNationalMinimum,
];

// Every sub-type an environment criterion can take, including the exemption markers — used to
// identify environment rows regardless of sub-type.
export const ENVIRONMENT_CRITERION_TYPES: readonly AwardCriterionType[] = [
  ...ENVIRONMENTAL_AWARD_CRITERION_TYPES,
  ...ENVIRONMENT_EXEMPTION_TYPES,
];

export const isEnvironmentCriterionType = (type: AwardCriterionType): boolean =>
  ENVIRONMENT_CRITERION_TYPES.includes(type);

// A § 5c exemption is stored as a single exemption marker criterion (one of three). It is rendered as
// the justification box (never a criterion card) and filtered out of the weights table.
export const isExemptionMarker = (type: AwardCriterionType): boolean =>
  ENVIRONMENT_EXEMPTION_TYPES.includes(type);

// The active § 5c exemption type in a criteria list, if any. The BFF keeps at most one marker, so
// this returns the first — used to drive the exemption-reason selector and its template.
export const activeExemptionType = (
  criteria: readonly { type: AwardCriterionType }[],
): AwardCriterionType | null => criteria.find((c) => isExemptionMarker(c.type))?.type ?? null;

// Whether the buyer has chosen a § 5c exemption: the criteria list holds an exemption marker AND no
// weighted environment criterion. A real environment criterion wins over the marker (the server drops
// the marker), so adding one is enough to leave the exemption — mirror that here so the UI doesn't
// flash the exemption box while an environment row is being added.
export const isExemption = (criteria: readonly { type: AwardCriterionType }[]): boolean =>
  criteria.some((c) => isExemptionMarker(c.type)) &&
  !criteria.some((c) => isEnvironmentCriterionType(c.type) && !isExemptionMarker(c.type));

// Collapse any environment sub-type to the single "Miljø" dropdown value.
export const criterionTypeSelectValue = (type: AwardCriterionType): string =>
  isEnvironmentCriterionType(type) ? ENVIRONMENT_GROUP_VALUE : type;

// Which criterion type a requirement is filed under when it is kept in the library. A library
// requirement stands alone, so it has to name its host — but a criterion's environment sub-type is not
// the buyer's to pick: the BFF re-derives it from the total weighting (`deriveEnvironmentType`), and a
// § 5c exemption is a marker with no requirements under it at all. Every environment variant therefore
// files under the family value the library accepts. Mirrors LibraryContent.SUB_CRITERION_HOST_TYPES,
// whose `require` rejects QUALITY_ENVIRONMENTAL_SPECIFICATION — the sub-type a criterion silently takes
// the moment the environment weighting drops below the 30 % floor.
export const libraryHostCriterionType = (type: AwardCriterionType): AwardCriterionType =>
  isEnvironmentCriterionType(type) ? AwardCriterionType.QualityEnvironmentalCriteria : type;

// Applies a picked type to a criterion. "ENVIRONMENT" is a UI-only grouping value, not an
// AwardCriterionType — the BFF derives the real sub-type (criteria vs specification) from the total
// environment weighting, so tag the row as the environment family and seed the § 5b floor when it has
// no weight yet. Shared by the add menu and the drawer's type picker so the two cannot drift.
export const withCriterionType = (criterion: AwardCriterionDto, selectValue: string): AwardCriterionDto => {
  if (selectValue !== ENVIRONMENT_GROUP_VALUE) {
    return { ...criterion, type: selectValue as AwardCriterionType };
  }
  const next: AwardCriterionDto = { ...criterion, type: AwardCriterionType.QualityEnvironmentalCriteria };
  return criterion.weight?.number == null
    ? { ...next, weight: { number: MIN_ENVIRONMENT_WEIGHTING_PERCENT, type: WeightType.Percentage } }
    : next;
};

// A blank criterion of the picked type, ready for the name to be typed inline in the table. The id is
// minted by the caller, so this stays pure.
export const emptyCriterion = (selectValue: string): AwardCriterionDto =>
  withCriterionType(
    { type: AwardCriterionType.Quality, name: "", description: "", weight: undefined },
    selectValue
  );

// The BFF derives the weighted sub-type from the total environment weighting, so it is never a name
// the buyer chose; the § 5c exemptions are chosen and keep their own.
export const criterionTypeLabelKey = (type: AwardCriterionType): string =>
  ENVIRONMENTAL_AWARD_CRITERION_TYPES.includes(type) ? AwardCriterionType.QualityEnvironmentalCriteria : type;

// anskaffelsesloven § 5b: minimum climate/environment weighting before a justification is required.
// NOTE: the BFF is the source of truth for this rule — `AwardCriteria.normalized()` re-derives every
// criterion's sub-type and the gate enforces the 30 % floor server-side on every write. This client
// copy (threshold + the type sets) exists only for instant UX (live dropdown,
// badge, prefilled box) and cannot be shared across the Kotlin/TypeScript boundary; keep it in sync.
export const MIN_ENVIRONMENT_WEIGHTING_PERCENT = 30;

// The climate/environment award rule (anskaffelsesloven § 5b/§ 5c) applies only to Del II/III procurements.
export const environmentRuleApplies = (foaPart: FoaPart | null | undefined): boolean =>
  foaPart === FoaPart.PartIi || foaPart === FoaPart.PartIii;

// NOTE: the criteria-vs-specification sub-type (vei A vs vei B) is derived by the BFF from the SUMMED
// environment weighting (the 30 % floor is on the total, so two 15 % criteria together are vei A) — see
// `AwardCriteria.normalized()`. The client never classifies it per criterion; it just tags the row as
// the environment family and lets the BFF (source of truth) settle criteria vs spec on each write.

// ── Exclusion Grounds Category ──────────────────────────────────────
//
// Categories A-G from EHF ESPD 1.2 used to group ESPD Part III (avvisningsgrunner).
// Only the EXCLUSION_* variants of EspdCriterionCategory are reachable here; the
// SELECTION_* and OTHER_* variants are filtered out by the BFF taxonomy endpoint.
// See docs/adr/005-exclusion-grounds.md.
export const EXCLUSION_GROUNDS_CATEGORY_OPTIONS = [
  { value: EspdCriterionCategory.ExclusionConvictions },
  { value: EspdCriterionCategory.ExclusionContributions },
  { value: EspdCriterionCategory.ExclusionSocial },
  { value: EspdCriterionCategory.ExclusionBusiness },
  { value: EspdCriterionCategory.ExclusionMisconduct },
  { value: EspdCriterionCategory.ExclusionConflict },
  { value: EspdCriterionCategory.ExclusionNational },
] as const;
